/**
 * DIOICA Glitch Engine & AntiGravity Logic
 */

class DioicaPortal {
  constructor() {
    this.canvas = document.getElementById('glitch-canvas');
    if (!this.canvas) return;

    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.grid = document.querySelector('.survivalist-grid');
    this.isUnlocked = false;
    this.isDrawing = false;
    this.revealThreshold = 0.5; // 50%

    this.init();
  }

  init() {
    this.resize();
    this.loadGlitchEngine();
    this.bindEvents();
    this.initAntiGravity();
    this.initShakeDetection();
  }

  resize() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    this.drawInitialNoise();
  }

  drawInitialNoise() {
    const w = this.canvas.width;
    const h = this.canvas.height;
    const idata = this.ctx.createImageData(w, h);
    const buffer32 = new Uint32Array(idata.data.buffer);

    for (let i = 0; i < buffer32.length; i++) {
      if (Math.random() < 0.5) buffer32[i] = 0xff000000; // Black
      else buffer32[i] = 0xff333333; // Dark Grey noise
    }

    this.ctx.putImageData(idata, 0, 0);

    // Add some random blue glitch lines
    for (let i = 0; i < 10; i++) {
      this.ctx.fillStyle = '#00f2ff';
      this.ctx.fillRect(0, Math.random() * h, w, 1);
    }
  }

  loadGlitchEngine() {
    const render = () => {
      if (this.isUnlocked) return;

      // Randomly inject RGB split or noise spikes
      if (Math.random() > 0.9) {
        this.injectSpike();
      }

      requestAnimationFrame(render);
    };
    render();
  }

  injectSpike() {
    const x = Math.random() * this.canvas.width;
    const y = Math.random() * this.canvas.height;
    const w = Math.random() * 200;
    const h = 2;

    this.ctx.save();
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.fillStyle = `rgba(0, 242, 255, ${Math.random()})`;
    this.ctx.fillRect(x, y, w, h);
    this.ctx.restore();
  }

  bindEvents() {
    window.addEventListener('resize', () => this.resize());

    const start = (e) => {
      this.isDrawing = true;
      this.scratch(e);
    };
    const end = () => {
      this.isDrawing = false;
      this.checkReveal();
    };
    const move = (e) => {
      if (this.isDrawing) this.scratch(e);
    };

    this.canvas.addEventListener('mousedown', start);
    this.canvas.addEventListener('touchstart', start);
    this.canvas.addEventListener('mousemove', move);
    this.canvas.addEventListener('touchmove', move);
    window.addEventListener('mouseup', end);
    window.addEventListener('touchend', end);

    // Micro-glitch on product click
    document.querySelectorAll('.product-link').forEach(link => {
      link.addEventListener('click', (e) => {
        const flash = document.createElement('div');
        flash.className = 'glitch-flash';
        document.body.appendChild(flash);
      });
    });
  }

  scratch(e) {
    const rect = this.canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    this.ctx.globalCompositeOperation = 'destination-out';
    this.ctx.beginPath();
    this.ctx.arc(x, y, 80, 0, Math.PI * 2); // Increased to 80px for brutal cleaning
    this.ctx.fill();
  }

  checkReveal() {
    if (this.isUnlocked) return;

    const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    const data = imageData.data;
    let transparentPixels = 0;

    for (let i = 3; i < data.length; i += 4) {
      if (data[i] === 0) transparentPixels++;
    }

    const percentage = transparentPixels / (this.canvas.width * this.canvas.height);

    if (percentage > this.revealThreshold) {
      this.unlock();
    }
  }

  unlock() {
    this.isUnlocked = true;
    this.canvas.classList.add('flicker-out');
    document.body.classList.add('dioica-resolved'); // Trigger light theme transition

    // Dismiss CTA instantly
    const cta = document.getElementById('glitch-cta');
    if (cta) cta.style.opacity = '0';

    setTimeout(() => {
      this.canvas.style.display = 'none';
      if (cta) cta.style.display = 'none';
      this.grid.classList.remove('blurred');
      this.grid.style.pointerEvents = 'auto';

      // Reveal terminal nav
      const terminalNav = document.getElementById('terminal-nav');
      if (terminalNav) {
        terminalNav.classList.add('revealed');
      }
    }, 500);
  }

  initAntiGravity() {
    const items = document.querySelectorAll('[data-antigravity]');

    window.addEventListener('deviceorientation', (e) => {
      if (!this.isUnlocked) return;

      const tiltX = e.beta;  // -180 to 180
      const tiltY = e.gamma; // -90 to 90

      const moveX = tiltY / 5;
      const moveY = (tiltX - 45) / 5; // Assuming 45deg is neutral holding

      items.forEach((item, index) => {
        const depth = (index % 3 + 1) * 2;
        item.style.transform = `translate(${moveX * depth}px, ${moveY * depth}px)`;
      });
    });

    // Fallback for Desktop (Mouse Parallax)
    window.addEventListener('mousemove', (e) => {
      if (!this.isUnlocked || window.DeviceOrientationEvent && 'ontouchstart' in window) return;

      const moveX = (e.clientX - window.innerWidth / 2) / 50;
      const moveY = (e.clientY - window.innerHeight / 2) / 50;

      items.forEach((item, index) => {
        const depth = (index % 3 + 1) * 2;
        item.style.transform = `translate(${moveX * depth}px, ${moveY * depth}px)`;
      });
    });
  }

  initShakeDetection() {
    let lastX, lastY, lastZ;
    let threshold = 15;

    window.addEventListener('devicemotion', (e) => {
      const acc = e.accelerationIncludingGravity;
      if (!acc) return;

      const deltaX = Math.abs(acc.x - lastX);
      const deltaY = Math.abs(acc.y - lastY);
      const deltaZ = Math.abs(acc.z - lastZ);

      if (deltaX + deltaY + deltaZ > threshold) {
        document.getElementById('terminal-nav').classList.add('revealed');
      }

      lastX = acc.x;
      lastY = acc.y;
      lastZ = acc.z;
    });

    // Special swipe reveal fallback
    let startY;
    window.addEventListener('touchstart', (e) => startY = e.touches[0].clientY);
    window.addEventListener('touchend', (e) => {
      const deltaY = startY - e.changedTouches[0].clientY;
      if (deltaY > 200) { // Long swipe up
        document.getElementById('terminal-nav').classList.add('revealed');
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new DioicaPortal();
});
